/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {defineMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import {
    IconButton,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    useDisclosure,
    useToast
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    AuthHelpers,
    useAuthHelper,
    useCustomer,
    useCustomerId,
    useCustomerType,
    useCustomerBaskets,
    useShopperBasketsMutation,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import LoginForm from '../components/login'
import RegisterForm from '../components/register'
import PasswordlessEmailConfirmation from '@salesforce/retail-react-app/app/components/email-confirmation/index'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {
    API_ERROR_MESSAGE,
    CREATE_ACCOUNT_FIRST_ERROR_MESSAGE,
    FEATURE_UNAVAILABLE_ERROR_MESSAGE,
    LOGIN_TYPES,
    PASSWORDLESS_ERROR_MESSAGES,
    USER_NOT_FOUND_ERROR
} from '@salesforce/retail-react-app/app/constants'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {usePrevious} from '@salesforce/retail-react-app/app/hooks/use-previous'
import {usePasswordReset} from './use-password-reset'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {isAbsoluteURL} from '@salesforce/retail-react-app/app/page-designer/utils'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {
    registerModal,
    registerModalCloseButton,
    registerModalContent
} from '../pages/registration/index.style'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useCustomerSearch from './useCustomerSearch'
import ResetPasswordForm from '../components/reset-password'
import {parseApiError} from '../components/forms/parseApiError/parseApiError'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {pushAnalyticsEvent} from '../../../overrides/app/utills/analytics'

async function computeSHA256(input) {
    if (typeof window === 'undefined' || !window.crypto || !window.crypto.subtle) {
        return ''
    }
    try {
        const encoder = new TextEncoder()
        const data = encoder.encode(input.toLowerCase())
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
    } catch {
        return ''
    }
}

export const LOGIN_VIEW = 'login'
export const REGISTER_VIEW = 'register'
export const PASSWORD_VIEW = 'password'
export const EMAIL_VIEW = 'email'

const LOGIN_ERROR = defineMessage({
    defaultMessage: "Something's not right with your email or password. Try again.",
    id: 'auth_modal.error.incorrect_email_or_password'
})

export const AuthModal = ({
    initialView = LOGIN_VIEW,
    initialEmail = '',
    onLoginSuccess = noop,
    onRegistrationSuccess = noop,
    isOpen,
    onOpen,
    onClose,
    isPasswordlessEnabled = false,
    isSocialEnabled = false,
    idps = [],
    ...props
}) => {
    const {formatMessage} = useIntl()
    const customerId = useCustomerId()
    const {isRegistered, customerType} = useCustomerType()
    const prevAuthType = usePrevious(customerType)

    const customer = useCustomer(
        {parameters: {customerId}},
        {enabled: !!customerId && isRegistered}
    )

    const navigate = useNavigation()
    const [currentView, setCurrentView] = useState(initialView)
    const form = useForm()
    const toast = useToast()

    const emailValue = form.watch('email')

    useEffect(() => {
        if (emailValue && typeof window !== 'undefined') {
            localStorage.setItem('passwordResetEmail', emailValue)
        }
    }, [emailValue])
    const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C)
    const register = useAuthHelper(AuthHelpers.Register)
    const appOrigin = useAppOrigin()
    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')
    const registerCustomerMutation = useShopperCustomersMutation('registerCustomer')

    const [loginType, setLoginType] = useState(LOGIN_TYPES.PASSWORD)
    const [hasSearchedCustomer, setHasSearchedCustomer] = useState(false)
    const [passwordlessLoginEmail, setPasswordlessLoginEmail] = useState(initialEmail)
    const [emailCustomerSearch, setEmailCustomerSearch] = useState(false)
    const {getPasswordResetToken} = usePasswordReset()
    const authorizePasswordlessLogin = useAuthHelper(AuthHelpers.AuthorizePasswordless)
    const passwordlessConfigCallback = getConfig().app.login?.passwordless?.callbackURI
    const callbackURL = isAbsoluteURL(passwordlessConfigCallback)
        ? passwordlessConfigCallback
        : `${appOrigin}${passwordlessConfigCallback}`

    const {data: baskets} = useCustomerBaskets(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer, keepPreviousData: true}
    )
    const mergeBasket = useShopperBasketsMutation('mergeBasket')

    const {data: searchCustomData, isLoading: isCustomerSearchLoading} = useCustomerSearch(
        emailCustomerSearch,
        !hasSearchedCustomer
    )
    const [registerInProgress, setRegisterInProgress] = useState(false)

    const onLoginSuccessRedirect = () => {
        navigate('/account')
    }

    const submitForm = async (data) => {
        form.clearErrors()

        const handlePasswordlessLogin = async (email) => {
            try {
                const redirectPath = window.location.pathname + window.location.search
                await authorizePasswordlessLogin.mutateAsync({
                    userid: email,
                    callbackURI: `${callbackURL}?redirectUrl=${redirectPath}`
                })
                setCurrentView(EMAIL_VIEW)
            } catch (error) {
                const message = USER_NOT_FOUND_ERROR.test(error.message)
                    ? formatMessage(CREATE_ACCOUNT_FIRST_ERROR_MESSAGE)
                    : PASSWORDLESS_ERROR_MESSAGES.some((msg) => msg.test(error.message))
                    ? formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE)
                    : formatMessage(API_ERROR_MESSAGE)
                form.setError('global', {type: 'manual', message})
            }
        }

        return {
            login: async (data) => {
                if (loginType === LOGIN_TYPES.PASSWORD) {
                    try {
                        await login.mutateAsync({
                            username: data.email,
                            password: data.password
                        })
                        if (typeof window !== 'undefined') {
                            localStorage.removeItem('passwordResetEmail')
                        }
                        const hasBasketItem = baskets?.baskets?.[0]?.productItems?.length > 0
                        const hasGiftcertificate =
                            baskets?.baskets?.[0]?.giftCertificateItems?.length > 0

                        // we only want to merge basket when the user is logged in as a recurring user
                        // only recurring users trigger the login mutation, new user triggers register mutation
                        // this logic needs to stay in this block because this is the only place that tells if a user is a recurring user
                        // if you change logic here, also change it in login page
                        const shouldMergeBasket =
                            (hasBasketItem || hasGiftcertificate) && prevAuthType === 'guest'
                        if (shouldMergeBasket) {
                            mergeBasket.mutate({
                                headers: {
                                    // This is not required since the request has no body
                                    // but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
                                    'Content-Type': 'application/json'
                                },
                                parameters: {
                                    createDestinationBasket: true,
                                    productItemMergeMode: 'sum_quantities'
                                }
                            })
                        }
                        if (typeof window !== 'undefined' && window.dataLayer) {
                            await pushAnalyticsEvent({
                                eventName: 'loginToAccount',
                                userId: customer?.data?.customerId || customerId || '',
                                email: data.email,
                                registered: customer?.isRegistered ?? true,
                                phone: customer?.data?.phoneMobile || ''
                            })
                        }
                        onLoginSuccessRedirect()
                    } catch (error) {
                        const errorBody = await parseApiError(error, form)
                        if (/Unauthorized/i.test(errorBody?.status_code)) {
                            form.setError('global', {
                                type: 'manual',
                                message: 'Password incorrect, please try again. '
                            })
                        } else {
                            form.setError('global', {type: 'manual', message: errorBody?.message})
                        }
                    }
                } else if (loginType === LOGIN_TYPES.PASSWORDLESS) {
                    setPasswordlessLoginEmail(data.email)
                    await handlePasswordlessLogin(data.email)
                }
            },
            register: async (data) => {
                setRegisterInProgress(true)
                const body = {
                    customer: {
                        firstName: data.firstName,
                        lastName: data.lastName,
                        email: data.email,
                        login: data.email,
                        phoneMobile: data.mobile,
                        ...(data.birthday && data.birthday !== '' && {birthday: data.birthday})
                    },
                    password: data.password
                }

                try {
                    // submit form
                    const userData = await registerCustomerMutation.mutateAsync(
                        {
                            body,
                            parameters: {
                                c_isSkipRecaptcha: false,
                                c_grecaptcharesponse: data?.recaptcha
                            }
                        },
                        {
                            onSuccess: async () => {
                                await login.mutateAsync({
                                    username: data.email,
                                    password: data.password
                                })
                                if (typeof window !== 'undefined') {
                                    localStorage.removeItem('passwordResetEmail')
                                }
                                setRegisterInProgress(false)
                                if (typeof window !== 'undefined' && window.dataLayer) {
                                    await pushAnalyticsEvent({
                                        eventName: 'createAccount',
                                        userId: userData.customerId,
                                        email: data.email,
                                        registered: true,
                                        phone: data.mobile || '',
                                        extraData: {
                                            first_name: data.firstName,
                                            last_name: data.lastName,
                                            birthday: data.birthday || ''
                                        }
                                    })
                                }
                                onLoginSuccessRedirect()
                            }
                        }
                    )
                } catch (error) {
                    setRegisterInProgress(false)
                    let errorBody
                    if (error?.response instanceof Response) {
                        try {
                            errorBody = await error.response.clone().json()
                        } catch {
                            const text = await error.response.text()
                            try {
                                errorBody = JSON.parse(text)
                            } catch {
                                errorBody = {message: text}
                            }
                        }
                    }

                    if (errorBody?.title === 'Login Already In Use') {
                        form.setError('global', {
                            type: 'manual',
                            message: formatMessage({
                                id: 'global.error.api_login_already_in_use',
                                defaultMessage:
                                    'Looks like you’re already a member! Sign in and add your phone number for easy look-up in store.'
                            })
                        })
                    } else if (errorBody?.title === 'Customer Already Registered') {
                        form.setError('global', {
                            type: 'manual',
                            message: formatMessage({
                                id: 'global.error.api_login_already_in_use',
                                defaultMessage:
                                    'Looks like you’re already a member! Sign in and add your phone number for easy look-up in store.'
                            })
                        })
                    } else if (errorBody?.title === 'Invalid Password') {
                        form.setError('global', {
                            type: 'manual',
                            message: formatMessage({
                                id: 'global.error.api_invalid_password',
                                defaultMessage: "The password doesn't meet the requirements."
                            })
                        })
                    } else if (errorBody?.statusCode === 'BUS-1003') {
                        form.setError('global', {
                            type: 'manual',
                            message: formatMessage({
                                id: 'global.error.recaptha_failed.label',
                                defaultMessage: 'Please verify that you are not a robot.'
                            })
                        })
                    } else if (errorBody?.statusCode === 'BUS-2003') {
                        form.setError('global', {
                            type: 'manual',
                            message: formatMessage({
                                id: 'global.error.required.fields.label',
                                defaultMessage: 'Required details are missing for the user.'
                            })
                        })
                    } else {
                        form.setError('global', {
                            type: 'manual',
                            message: formatMessage(API_ERROR_MESSAGE)
                        })
                    }
                }
            },
            password: async (data) => {
                try {
                    await getPasswordResetToken(data.email)
                } catch (e) {
                    const message =
                        e.response?.status === 400
                            ? formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE)
                            : formatMessage(API_ERROR_MESSAGE)
                    form.setError('global', {type: 'manual', message})
                }
            },
            email: async () => {
                await handlePasswordlessLogin(passwordlessLoginEmail)
            }
        }[currentView](data)
    }

    // Reset form and local state when opening the modal
    useEffect(() => {
        if (isOpen) {
            setLoginType(LOGIN_TYPES.PASSWORD)
            setCurrentView(initialView)
            form.reset()
        }
    }, [isOpen])

    // Auto-focus the first field in each form view
    useEffect(() => {
        const initialField = {
            [LOGIN_VIEW]: 'email',
            [REGISTER_VIEW]: 'firstName',
            [PASSWORD_VIEW]: 'email'
        }[currentView]
        const fieldsRef = form.control?.fieldsRef?.current
        fieldsRef?.[initialField]?.ref.focus()
    }, [form.control?.fieldsRef?.current])

    useEffect(() => {
        form.reset()

        if (currentView === PASSWORD_VIEW) {
            const storedEmail =
                typeof window !== 'undefined'
                    ? localStorage.getItem('passwordResetEmail') || ''
                    : ''

            if (storedEmail) {
                setTimeout(() => {
                    form.setValue('email', storedEmail)
                }, 10)
            }
        }
    }, [currentView, form])

    useEffect(() => {
        setPasswordlessLoginEmail(initialEmail)
    }, [initialEmail])

    useEffect(() => {
        // Lets determine if the user has either logged in, or registed.
        const loggingIn = currentView === LOGIN_VIEW
        const registering = currentView === REGISTER_VIEW
        const isNowRegistered = isOpen && isRegistered && (loggingIn || registering)
        // If the customer changed, but it's not because they logged in or registered. Do nothing.
        if (!isNowRegistered) {
            return
        }

        // We are done with the modal.
        onClose()

        if (typeof window !== 'undefined') {
            localStorage.removeItem('passwordResetEmail')
        }

        // Show a toast only for those registed users returning to the site.
        if (loggingIn) {
            toast({
                variant: 'subtle',
                title: `${formatMessage(
                    {
                        defaultMessage: 'Welcome {name},',
                        id: 'auth_modal.info.welcome_user'
                    },
                    {
                        name: customer.data?.firstName || ''
                    }
                )}`,
                description: `${formatMessage({
                    defaultMessage: "You're now signed in.",
                    id: 'auth_modal.description.now_signed_in'
                })}`,
                status: 'success',
                position: 'top-right',
                isClosable: true
            })

            // Execute action to be performed on successful login
            onLoginSuccessRedirect()
        }

        if (registering) {
            // Execute action to be performed on successful registration
            onRegistrationSuccess()
        }
    }, [isRegistered])

    useEffect(() => {
        if (hasSearchedCustomer && searchCustomData && !searchCustomData?.customerExists) {
            setCurrentView(REGISTER_VIEW)
        }
    }, [hasSearchedCustomer, searchCustomData])

    useEffect(() => {
        setHasSearchedCustomer(false)
        setEmailCustomerSearch(false)
    }, [isOpen])

    const onBackToSignInClick = () =>
        initialView === PASSWORD_VIEW ? onClose() : setCurrentView(LOGIN_VIEW)

    const handleResendLink = async () => {
        try {
            const email = form.getValues('email')
            await getPasswordResetToken(email)
        } catch (error) {
            const message = formatMessage(API_ERROR_MESSAGE)
            form.setError('global', {type: 'manual', message})
        }
    }

    const handleEmailSubmit = async () => {
        setEmailCustomerSearch(form.getValues('email'))
        const valid = await form.trigger('email')
        if (valid) {
            setHasSearchedCustomer(true)
            form.setValue('password', '')
        }
    }
    const handleEditEmailClick = () => {
        form.setValue('password', '')
        form.resetField('password')
        setHasSearchedCustomer(false)
        setEmailCustomerSearch(false)
    }

    return (
        <Modal
            size="sm"
            closeOnOverlayClick={false}
            data-testid="sf-auth-modal"
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            {...props}
        >
            <ModalOverlay />
            <ModalContent sx={registerModalContent}>
                <IconButton
                    id="auth-modal-close-trigger"
                    icon={
                        <img
                            src={getAssetUrl('static/img/pdp/close.svg')}
                            height="15"
                            width="15"
                            alt="close"
                        />
                    }
                    variant="unstyled"
                    onClick={onClose}
                    sx={registerModalCloseButton}
                />
                <ModalBody p={0} bg="white">
                    {registerInProgress && <LoadingSpinner />}
                    {!form.formState.isSubmitSuccessful && currentView === LOGIN_VIEW && (
                        <LoginForm
                            form={form}
                            submitForm={submitForm}
                            hideEmail={searchCustomData?.customerExists}
                            hidePassword={!searchCustomData?.customerExists}
                            isCustomerSearchLoading={isCustomerSearchLoading}
                            handleEmailSubmit={handleEmailSubmit}
                            handleEditEmailClick={handleEditEmailClick}
                            clickCreateAccount={() => setCurrentView(REGISTER_VIEW)}
                            handlePasswordlessLoginClick={() =>
                                setLoginType(LOGIN_TYPES.PASSWORDLESS)
                            }
                            handleForgotPasswordClick={() => setCurrentView(PASSWORD_VIEW)}
                            isPasswordlessEnabled={isPasswordlessEnabled}
                            isSocialEnabled={isSocialEnabled}
                            idps={idps}
                            setLoginType={setLoginType}
                        />
                    )}
                    {!form.formState.isSubmitSuccessful && currentView === REGISTER_VIEW && (
                        <RegisterForm
                            form={form}
                            submitForm={submitForm}
                            clickSignIn={onBackToSignInClick}
                        />
                    )}
                    {currentView === PASSWORD_VIEW && (
                        <ResetPasswordForm
                            form={form}
                            submitForm={submitForm}
                            clickSignIn={onBackToSignInClick}
                            onResendLink={handleResendLink}
                        />
                    )}
                    {currentView === EMAIL_VIEW && (
                        <PasswordlessEmailConfirmation
                            form={form}
                            submitForm={submitForm}
                            email={passwordlessLoginEmail}
                        />
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

AuthModal.propTypes = {
    initialView: PropTypes.oneOf([LOGIN_VIEW, REGISTER_VIEW, PASSWORD_VIEW, EMAIL_VIEW]),
    initialEmail: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onLoginSuccess: PropTypes.func,
    onRegistrationSuccess: PropTypes.func,
    isPasswordlessEnabled: PropTypes.bool,
    isSocialEnabled: PropTypes.bool,
    idps: PropTypes.arrayOf(PropTypes.string)
}

/**
 *
 * @param {('register'|'login'|'password'|'email')} initialView - the initial view for the modal
 * @returns {Object} - Object props to be spread on to the AuthModal component
 */
export const useAuthModal = (initialView = LOGIN_VIEW) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {passwordless = {}, social = {}} = getConfig().app.login || {}

    return {
        initialView,
        isOpen,
        onOpen,
        onClose,
        isPasswordlessEnabled: !!passwordless?.enabled,
        isSocialEnabled: !!social?.enabled,
        idps: social?.idps
    }
}
